/**
 * Exames que a IA sugeriu na consulta e o médico marcou para pedir.
 *
 * Fica no `localStorage`, e não no servidor, porque é rascunho: vale entre clicar
 * na sugestão e abrir o pedido de exames, quase sempre na mesma sessão. Gravar no
 * banco exigiria um model, uma migration e um ciclo de vida ("quando isto expira?")
 * para um dado que morre no mesmo dia.
 *
 * O que se guarda é o TEXTO da sugestão ("Hemograma completo"), não um `catalogId`:
 * a IA lê a transcrição e escreve em português, sem saber o que está cadastrado. O
 * casamento com o catálogo acontece ao abrir o pedido, pela busca ranqueada do
 * próprio catálogo — que já tolera acento e erro de digitação.
 *
 * Escopo por PACIENTE: o médico atende um em seguida do outro, e sugestão vazada de
 * um paciente para outro entraria num pedido de exame alheio.
 */

const STORAGE_PREFIX = 'exam-suggestions'

/** Sugestão pendente: o texto como a IA escreveu, e de onde ele veio. */
export interface PendingExamSuggestion {
    term: string
    /**
     * Item do catálogo, quando a IA achou pela ferramenta de busca. Com ele o
     * pedido marca o exame direto — sem ele, o modal cai na busca por texto, que
     * é o caminho de quem tem análise gerada antes das ferramentas existirem.
     */
    catalogId?: number
    /** Consulta que originou a sugestão — só para rastreio. */
    consultationId?: number
    addedAt: string
}

const storageKey = (clientId: number): string => `${STORAGE_PREFIX}:${clientId}`

const normalize = (term: string): string =>
    (term ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')

/**
 * Toda leitura e escrita é protegida: `localStorage` lança em aba anônima com dados
 * de site bloqueados, e um pedido de exame não pode deixar de abrir por causa de um
 * rascunho.
 */
export function readExamSuggestions(clientId: number): PendingExamSuggestion[] {
    try {
        const raw = localStorage.getItem(storageKey(clientId))
        if (!raw) return []
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed.filter(item => !!item?.term) : []
    } catch {
        return []
    }
}

/** Acrescenta uma sugestão. Repetida (ignorando acento e caixa) não duplica. */
export function addExamSuggestion(
    clientId: number,
    term: string,
    options?: { catalogId?: number; consultationId?: number }
): PendingExamSuggestion[] {
    const trimmed = (term ?? '').trim()
    if (!trimmed) return readExamSuggestions(clientId)

    const current = readExamSuggestions(clientId)
    // Repetido não duplica — mas se agora veio com `catalogId` e antes não tinha,
    // o id é gravado: é o que tira o modal da busca por texto.
    const existingIndex = current.findIndex(
        item =>
            normalize(item.term) === normalize(trimmed) ||
            (!!options?.catalogId && item.catalogId === options.catalogId)
    )
    if (existingIndex >= 0) {
        if (options?.catalogId && !current[existingIndex].catalogId) {
            const merged = [...current]
            merged[existingIndex] = {
                ...merged[existingIndex],
                catalogId: options.catalogId,
            }
            write(clientId, merged)
            return merged
        }
        return current
    }

    const next = [
        ...current,
        {
            term: trimmed,
            catalogId: options?.catalogId,
            consultationId: options?.consultationId,
            addedAt: new Date().toISOString(),
        },
    ]
    write(clientId, next)
    return next
}

export function removeExamSuggestion(
    clientId: number,
    term: string
): PendingExamSuggestion[] {
    const next = readExamSuggestions(clientId).filter(
        item => normalize(item.term) !== normalize(term)
    )
    write(clientId, next)
    return next
}

/** Chamado quando o pedido é criado: o rascunho cumpriu o papel. */
export function clearExamSuggestions(clientId: number): void {
    try {
        localStorage.removeItem(storageKey(clientId))
    } catch {
        // Sem storage não havia rascunho para limpar.
    }
}

export function hasExamSuggestion(clientId: number, term: string): boolean {
    return readExamSuggestions(clientId).some(
        item => normalize(item.term) === normalize(term)
    )
}

function write(clientId: number, value: PendingExamSuggestion[]): void {
    try {
        localStorage.setItem(storageKey(clientId), JSON.stringify(value))
    } catch {
        // Cota estourada ou storage bloqueado: a sugestão simplesmente não persiste.
    }
}
